import { useEffect, useRef } from "react";
import { getRuntimeApiBase } from "../lib/runtime-api-base";

const API_BASE = getRuntimeApiBase();
const DEV = import.meta.env.DEV;

export function useAuthBackendSync(
  userId: string | null,
  token: string | null,
) {
  const bootstrappedUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || !token) return;
    if (bootstrappedUserRef.current === userId) return;

    bootstrappedUserRef.current = userId;
    fetch(`${API_BASE}/api/auth/bootstrap`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch((error) => {
      bootstrappedUserRef.current = null;
      DEV && console.warn("[Auth] user bootstrap failed:", error);
    });
  }, [userId, token]);

  useEffect(() => {
    if (!userId || !token) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    async function syncSetUser(reason: string) {
      try {
        const res = await fetch(`${API_BASE}/api/relay/set-user`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        });
        if (!cancelled && !res.ok && res.status !== 401) {
          console.debug(
            `[AuthContext] relay set-user (${reason}) non-ok:`,
            res.status,
          );
        }
      } catch {
        // Local backend down; the next poll will retry.
      }
    }

    async function reconcile() {
      try {
        const res = await fetch(`${API_BASE}/api/relay/connector-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as {
          connected: boolean;
          matchesCaller: boolean;
        };
        if (!data.matchesCaller || !data.connected) {
          void syncSetUser(
            !data.matchesCaller ? "identity-drift" : "disconnected",
          );
        }
      } catch {
        // Local backend unreachable; do nothing this tick.
      }
    }

    void syncSetUser("initial");
    timer = setInterval(reconcile, 30_000);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [userId, token]);
}
